import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { fetchFeedData } from "./UAFetchFeedData";
import { Heart, MessageCircle, Share2, MoreHorizontal, Bookmark, Archive, Flag, Monitor } from "lucide-react";
import {
  MoreButton,
  CloseButton,
} from "../../../../UI-comp/ActionButtons";
import ShareButton from "../../../../common/Actions/shareButton";
import LikeButton from "../../../../common/Actions/likeButton";
import SuggestButton from "../../../../common/Actions/suggestButton";
import MenuDropdown from "../../../../UI-comp/ManuDropdown";
import Carousel from "../../../../common/Carousel/Carousel";
import GridLayout from "../../../../common/Grid/gridMode";
import LightboxGallery from "../../../../common/LightBox/lightBox";
import HorizontalScrollDisplay from "../../../../common/HorizontalScroll/horizontalScroll";
import VerticalScroll from "../../../../common/VerticalScroll/verticalScroll";
import "../../Styles/UAFeedContent.css";
import "../../../Styles/common-styles.css";
import "../../Styles/UACommonStyles.css";
import "../../Styles/UserAccountGridLayout.css";
import ReelSection from "../reelSection";

// Main Feed Content Component
const UAFeedContent = ({ mediaDisplayMode, liUserId, activeFilter, setActiveFilter }) => {
  const [feedData, setFeedData] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isPostOpen, setIsPostOpen] = useState(true);
  const [openDropdownPostId, setOpenDropdownPostId] = useState(null);
  const [filteredFeedData, setFilteredFeedData] = useState([]);
  const [likedPosts, setLikedPosts] = useState({});

  const commentBoxRef = useRef(null);

  const handleMoreOptionClick = (postId) => {
    setOpenDropdownPostId((prevId) => (prevId === postId ? null : postId));
  };

  const handleOptionClick = (option, postId) => {
    setOpenDropdownPostId(null);
  };

  const handlePostLike = (postId) => {
    setLikedPosts((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };
  
  const handleClose = () => setIsPostOpen(false);

  const postmenuItems = [
    { label: "Save", action: (index) => handleOptionClick("Save", index) },
    { label: "Archive", action: (index) => handleOptionClick("Archive", index) },
    { label: "Report abuse", action: (index) => handleOptionClick("Report abuse", index) },
  ];

  const handleAddComment = (postId) => {
    if (newComment.trim()) {
      onAddComment(postId, newComment.trim());
      setNewComment("");
    }
  };

  const onAddComment = async (postId, commentText) => {
    const addedComment = {
      userId: `${liUserId}`,
      comment: commentText,
      timestamp: new Date().toISOString(),
    };

    setFeedData((prevFeedData) =>
      prevFeedData.map((user) => ({
        ...user,
        posts: user.posts.map((post) =>
          post.postId === postId
            ? { ...post, comments: [...(post.comments || []), addedComment] }
            : post
        ),
      }))
    );

    setTimeout(() => {
      if (commentBoxRef.current) {
        commentBoxRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    }, 100);
  };

  // Filter feed data based on active filter
  useEffect(() => {
    if (!feedData || feedData.length === 0) {
      setFilteredFeedData([]);
      return;
    }

    let filtered = feedData;

    switch (activeFilter.toLowerCase()) {
      case "feed":
        filtered = feedData;
        break;
      case "post":
        filtered = feedData.map((user) => ({
          ...user,
          posts: user.posts.filter((post) => post.userId === liUserId),
        }));
        break;
      case "saved":
        filtered = feedData.map((user) => ({
          ...user,
          posts: user.posts.filter((post) => post.isSaved === true),
        }));
        break;
      case "suggest":
        filtered = feedData.map((user) => ({
          ...user,
          posts: user.posts.filter((post) => post.isSuggested === true),
        }));
        break;
      case "shared":
        filtered = feedData.map((user) => ({
          ...user,
          posts: user.posts.filter((post) => post.isShared === true),
        }));
        break;
      case "archive":
        filtered = feedData.map((user) => ({
          ...user,
          posts: user.posts.filter((post) => post.isArchived === true),
        }));
        break;
      case "settings":
        filtered = [];
        break;
      default:
        filtered = feedData;
    }

    setFilteredFeedData(filtered.filter((user) => user.posts && user.posts.length > 0));
  }, [feedData, activeFilter, liUserId]);

  useEffect(() => {
    const loadFeedData = async () => {
      const data = await fetchFeedData();
      setFeedData(data || []);
    };
    loadFeedData();
  }, []);

  // Helper function to get comment count
  const getCommentCount = (comments) => {
    if (!comments) return 0;
    if (Array.isArray(comments)) return comments.length;
    return 0;
  };

  // ✅ Helper function to safely get likes count
  const getLikesCount = (likes) => {
    if (typeof likes === "number") return likes;
    if (typeof likes === "object" && likes !== null && likes.count) {
      return typeof likes.count === "number" ? likes.count : 0;
    }
    return 0;
  };

  // ✅ Helper function to safely get shares count
  const getSharesCount = (shares) => {
    if (typeof shares === "number") return shares;
    if (typeof shares === "object" && shares !== null && shares.count) {
      return typeof shares.count === "number" ? shares.count : 0;
    }
    return 0;
  };

  // Function to render post card
  const renderPostCard = (post, postIndex) => (
    <div key={post.postId} className="uacf-post-card space-y-4" style={{background: "rgba(36,36,45,0.2)"}}>
      {/* User Header */}
      <div className="uacf-user-container">
        <div className="uacf-user-info-wrapper">
          <div className="uacf-user-avatar-wrapper" style={{ marginLeft: 15}}>
            <img
              src={post.userimage}
              alt="User Avatar"
              className="tt-profile-image"
            />
          </div>

          <div className="uacf-user-name-wrapper">
            <p className="text-header">
              <Link
                to={`/user-account/${liUserId}/${post.userId}`}
                className="uacf-user-link"
                style={{ color: "white" }}
              >
                {post.userName}
              </Link>
            </p>
          <p
            className="text-label"
            style={{ color: "white", fontWeight: "normal" }}
          >
            <Link
              onClick={() =>
                window.location.href = `http://events.kleido.ai/location-page/${liUserId}?locationname=bangalore&year=2025`
              }
              className="uacf-location-link"
              style={{ color: "white", fontSize: 15 }}
            >
              {post.location}
            </Link>
            , {new Date(post.timestamp).toLocaleString()}
          </p>
          </div>

          <div className="uacf-user-action">
            <MoreButton
              color="transparent"
              onClick={() => handleMoreOptionClick(post.postId)}
            />
            {openDropdownPostId === post.postId && (
              <MenuDropdown
                isOpen={openDropdownPostId === post.postId}
                onClose={() => setOpenDropdownPostId(null)}
                items={postmenuItems.map((item) => ({
                  ...item,
                  action: () => item.action(post.postId),
                }))}
                className="uaf-profile-menu-dropdown"
              />
            )}
            <CloseButton color="transparent" onClick={handleClose} />
          </div>
        </div>
      </div>

      {/* Post Media */}
      <div className="uacf-feed-media">
        <p className="text-label uacf-post-description" style={{ marginLeft: 15, color: "white", marginTop: -10, fontWeight: "light" }}>
          {post.descriptions}
        </p>
        {Array.isArray(post.medias) && post.medias.length > 0 && (
          <div className="uacf-feed-media-container" >
            {mediaDisplayMode === "slideshow" ? (
              <Carousel items={post.medias} postData={post} />
            ) : mediaDisplayMode === "lightbox" ? (
              <LightboxGallery mediaFiles={post.medias} />
            ) : mediaDisplayMode === "horizontal-scroll" ? (
              <HorizontalScrollDisplay medias={post.medias} />
            ) : mediaDisplayMode === "vertical-scroll" ? (
              <VerticalScroll medias={post.medias} />
            ) : (
              <GridLayout mediaFiles={post.medias} />
            )}
          </div>
        )}
      </div>

      {/* Likes, Shares, Suggestions */}
      <div className="uacf-post-stats-wrapper" style={{display: "flex", flexDirection: "row" }}>
        <div style={{height: "100%", width: "50%", display: "flex", flexDirection: "row"}}>
        <button
          onClick={() => handlePostLike(post.postId)}
          className={`flex items-center gap-1.5 text-sm transition-colors ${
            likedPosts[post.postId] ? "text-red-500" : "text-muted-foreground hover:text-red-500"
          }`}
        >
          <Heart 
            size={17} 
            fill={likedPosts[post.postId] ? "currentColor" : "none"} 
            color={likedPosts[post.postId] ? "red" : "currentColor"}
          />
          {getLikesCount(post.likes) + (likedPosts[post.postId] ? 1 : 0)}
        </button>
        <button className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <MessageCircle size={17} style={{marginLeft: 30}} />
          {getCommentCount(post.comments)}
        </button>
        <button className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Share2 size={17} style={{marginLeft: 30}} />
          {getSharesCount(post.shares)}
        </button>
        </div>
        <div className="uacf-post-stats-right">
          <span className="text-label uacf-impressions-text">
            Gained {post.impressions || 100} impressions 👀 in last 10 hours
          </span>
        </div>
      </div>

      {/* Comments Section */}
      <div className="uacf-comments-wrapper">
        <div className="uacf-feed-comments">
          <p className="uacf-comments-title" style={{color: "white"}}>Comments:</p>
          <div className="comments-container">
            {Array.isArray(post.comments) && post.comments.length > 0 ? (
              post.comments.map((comment, index) => {
                // Safely extract comment properties
                const userId = comment?.userId || "Anonymous";
                const commentText = comment?.comment || "";
                const timestamp = comment?.timestamp ? new Date(comment.timestamp).toLocaleString() : "Just now";
                
                return (
                  <div
                    key={index}
                    className="uacf-comment-item"
                    ref={
                      index === post.comments.length - 1 ? commentBoxRef : null
                    }
                    style={{background: "rgba(36,36,45,0.2)"}}
                  >
                    <p className="text-label">
                      <Link
                        to={`/user-account/${liUserId}/${userId}`}
                        className="uacf-comment-link"
                        style={{color: "white"}}
                      >
                        <strong>{userId}:</strong> {commentText}
                      </Link>
                      <span style={{ fontSize: "15px", color: "white", marginLeft: "15px" }}>
                        ({timestamp})
                      </span>
                    </p>
                  </div>
                );
              })
            ) : (
              <p className="uacf-no-comments">No comments yet.</p>
            )}
          </div>
        </div>

        {/* Add Comment Input */}
        <div className="uacf-add-comment" style={{marginTop: -10}}>
          <input
            type="text"
            value={newComment}
            placeholder="Add a comment..."
            onChange={(e) => setNewComment(e.target.value)}
            className="uafc-comment-input"
            style={{background: "rgba(36,36,45,0.2)", marginTop: 0}}
          />
          <button
            onClick={() => handleAddComment(post.postId)}
            className="uafc-add-comment-button"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );

  // Create a flat list of all posts with their index
  const allPosts = [];
  filteredFeedData.forEach((user) => {
    user.posts.forEach((post) => {
      allPosts.push({ ...post, userName: user.posts[0].userName, userimage: user.posts[0].userimage });
    });
  });

  return (
    <div className="uacf-feed-container">
      {filteredFeedData.length === 0 ? (
        <p className="text-label uacf-loading">
          {activeFilter === "Settings"
            ? "Settings page"
            : `No ${activeFilter.toLowerCase()} available`}
        </p>
      ) : (
        filteredFeedData.map((user) => (
          <div key={user.rootuser} className="uacf-user-feed">
            {user.posts.map((post, postIndex) => {
              // Calculate global post index
              const globalPostIndex = allPosts.findIndex((p) => p.postId === post.postId);

              return (
                <React.Fragment key={post.postId}>
                  {/* Render Post Card */}
                  {renderPostCard(post, postIndex)}

                  {/* Render Reel Section After First Post (globalPostIndex === 0) */}
                  {globalPostIndex === 0 && (
                    <div className="mb-4" style={{ margin: "20px 0", width: "100%" }}>
                      <ReelSection />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
};

export default UAFeedContent;
